/** Builder section 3 — the ad units: the rate card's standard mix, or the seller's own split. */

import React from 'react';
import { Button } from '../../../ui';
import UnitMixEditor from '../UnitMixEditor';
import { MEDIA_PLAN_UNIT_ORDER, type MediaPlanUnitDef, type MediaPlanUnitKey } from '../types';
import { fieldId, type PlanDraft } from '../planDraft';
import { fmtPct, hintStyle } from '../formStyles';
import FormSection from './FormSection';
import { FieldError, type ErrorFor } from './fields';

interface Props {
  draft: PlanDraft;
  update: (patch: Partial<PlanDraft>) => void;
  units: MediaPlanUnitDef[];
  /** The rate card's default split, once a quote has said what it is. */
  standardMix?: Partial<Record<MediaPlanUnitKey, number>>;
  errorFor: ErrorFor;
  disabled?: boolean;
}

/** "50% :15 NS · 50% CTV :30" */
export function mixSummary(mix: Partial<Record<MediaPlanUnitKey, number>>, units: readonly MediaPlanUnitDef[]): string {
  const label = (key: MediaPlanUnitKey) => units.find((u) => u.key === key)?.shortLabel || key;
  return MEDIA_PLAN_UNIT_ORDER.filter((k) => (mix[k] ?? 0) > 0)
    .map((k) => `${fmtPct(mix[k]!)} ${label(k)}`)
    .join(' · ');
}

const asStrings = (mix: Partial<Record<MediaPlanUnitKey, number>>): Partial<Record<MediaPlanUnitKey, string>> => {
  const out: Partial<Record<MediaPlanUnitKey, string>> = {};
  for (const k of MEDIA_PLAN_UNIT_ORDER) if ((mix[k] ?? 0) > 0) out[k] = String(mix[k]);
  return out;
};

const UnitsSection: React.FC<Props> = ({ draft, update, units, standardMix, errorFor, disabled = false }) => {
  const standard = standardMix && Object.keys(standardMix).length ? mixSummary(standardMix, units) : '';
  return (
    <FormSection id="mp-section-units" title="Ad units" note="Each unit's share of the CPM budget. Takeovers are flat-priced and costed first.">
      {draft.useCardMix ? (
        <div style={styles.row}>
          <span style={{ fontSize: 13.5, color: 'var(--fg-1)' }}>
            <strong>Standard mix</strong>
            {standard ? ` · ${standard}` : ' from the rate card'}
          </span>
          <Button size="sm" variant="subtle" disabled={disabled} onClick={() => update({ useCardMix: false, unitMix: standardMix && standard ? asStrings(standardMix) : draft.unitMix })}>
            Customize
          </Button>
        </div>
      ) : (
        <>
          <div id={fieldId('unitMix')} tabIndex={-1} style={{ outline: 'none' }}>
            <UnitMixEditor units={units} mix={draft.unitMix} onChange={(unitMix) => update({ unitMix })} disabled={disabled} />
          </div>
          <FieldError field="unitMix" message={errorFor('unitMix')} />
          <div>
            <Button size="sm" variant="ghost" disabled={disabled} onClick={() => update({ useCardMix: true })}>
              Use the standard mix
            </Button>
            {standard && <span style={{ ...hintStyle, marginLeft: 8 }}>{standard}</span>}
          </div>
        </>
      )}
    </FormSection>
  );
};

const styles: Record<string, React.CSSProperties> = {
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
};

export default UnitsSection;
